import React, { useEffect, useState, useRef } from "react";
import { ModeloSite } from "../../CentralVendedor";
import api from "../../../../../../../services/api";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { User } from "../../../../../../../entities/Security";

interface EditarModeloModalProps {
  onCloseParams: () => void;
  onCloseModelo: () => void;
  usuarioSite: ModeloSite | undefined;
  usuarioUMSId: string | undefined;
  _shortUrl?: string;
  reload: () => Promise<void>;
  user?: User;
}

interface FormDataProps {
  [key: string]: {
    value: any;
    description: string | undefined;
  };
}

interface ParamsMapProps {
  id: number;
  chave: string;
  valor: string;
  descricao: string;
  usuario_modelo_site_id: string;
  type: string;
}

export function useEditarModeloModal({
  onCloseParams,
  onCloseModelo,
  usuarioSite,
  usuarioUMSId,
  _shortUrl,
  reload,
  user,
}: EditarModeloModalProps) {
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [formData, setFormData] = useState<FormDataProps>({});
  const [paramsMap, setParamsMap] = useState<ParamsMapProps[]>([]);
  const [alert, setAlert] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shortUrl, setShortUrl] = useState<{
    isLoading: boolean;
    available: boolean;
    value: string | undefined;
  }>({
    isLoading: false,
    available: false,
    value: _shortUrl,
  });

  const [iframeURLQueryParams, setIframeURLQueryParams] = useState("");
  const debounceTimer = useRef<NodeJS.Timeout>();

  function handleClose() {
    clearParamsPreview();
    setFormData({});
    onCloseParams();
    onCloseModelo();
  }

  async function carregarParametros() {
    try {
      if (!usuarioSite || !usuarioUMSId) return;
      const { data: params } = await api.get<ParamsMapProps[]>(
        `/marketing/modelos-sites/parametros/${usuarioUMSId}`,
      );

      const siteParams: ParamsMapProps[] =
        JSON.parse(usuarioSite.params_map) || [];

      const updatedParamsMap = siteParams.map((param) => {
        const foundParam = params.find((uParam) => uParam.chave == param.chave);

        if (foundParam) foundParam.type = param.type;

        return foundParam || param;
      });

      setParamsMap(updatedParamsMap);
    } catch (error) {
      console.error(error);
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, dataset } = event.target;

    const isCheckbox = event.target.type === "checkbox";

    setFormData((prevData) => ({
      ...prevData,
      [name]: {
        value: isCheckbox ? checked : value,
        description: dataset.description,
      },
    }));
  };

  function onParamsPreview() {
    let queryParams = "";
    Object.entries(formData).forEach(([name, { value }]) => {
      queryParams += `&${name}=${value}`;
    });

    setIframeURLQueryParams(`${queryParams}`);
  }

  function clearParamsPreview() {
    setIframeURLQueryParams("");
  }

  async function onSubmit() {
    if (!usuarioUMSId) return;

    await handleSaveModelo();

    const formDataArray = Object.entries(formData).map(
      ([name, { value, description }]) => ({
        chave: name,
        valor: value,
        descricao: description,
      }),
    );

    try {
      setIsLoading(true);
      await api.post("/marketing/modelos-sites/parametros", {
        siteParams: formDataArray,
        usuario_modelo_site_id: usuarioUMSId,
      });

      carregarParametros();
      setAlert(
        <ReactBSAlert
          success
          style={{ display: "block" }}
          title="Sucesso!"
          onConfirm={() => {
            handleClose();
            setAlert(null);
            reload();
          }}
          onCancel={() => setAlert(null)}
          confirmBtnBsStyle="primary"
          confirmBtnText="Ok"
          btnSize=""
        >
          Página salva com sucesso!
        </ReactBSAlert>,
      );
    } catch (error) {
      console.error("Houve um erro ao tentar fazer a requisição.", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSaveModelo() {
    try {
      if (!usuarioSite) return;

      await api.post(`/marketing/modelos-sites/usuario/${usuarioUMSId}`, {
        modelo_site_id: usuarioSite.id,
        short_url: shortUrl.value,
      });
    } catch (error) {
      console.error(error);
    }
  }

  function handleShortUrlInput(
    e:
      | React.ChangeEvent<HTMLInputElement>
      | { target: { value: string | undefined } },
  ) {
    const normalizedValue = (e.target.value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\x00-\x7F]/g, "")
      .replace(" ", "-")
      .toLowerCase();

    setShortUrl((prev) => ({
      ...prev,
      isLoading: true,
      available: false,
      value: normalizedValue,
    }));

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post(
          `/marketing/modelos-sites/usuario/check-short-url/${usuarioUMSId}`,
          {
            short_url: normalizedValue,
          },
        );

        setShortUrl((prev) => {
          if (prev.value === normalizedValue) {
            return {
              isLoading: false,
              available: data.available,
              value: data.shortUrl,
            };
          }
          return prev;
        });
      } catch (err) {
        console.error(err);
        setShortUrl((prev) => {
          if (prev.value === normalizedValue) {
            return {
              ...prev,
              isLoading: false,
            };
          }
          return prev;
        });
      }
    }, 1000);
  }

  useEffect(() => {
    handleShortUrlInput({
      target: { value: _shortUrl ? _shortUrl : user?.name },
    });
    if (usuarioUMSId) carregarParametros();
  }, [usuarioSite]);

  return {
    onSubmit,
    handleInputChange,
    paramsMap,
    isFormVisible,
    setIsFormVisible,
    onParamsPreview,
    iframeURLQueryParams,
    clearParamsPreview,
    handleClose,
    alert,
    isLoading,
    shortUrl,
    handleShortUrlInput,
  };
}
